import { z } from "zod";
import { PAGE_TYPES, PAGE_STATUSES, type PageType } from "../schema/index.js";
import {
  CANONICAL_OUTPUT_KEYS,
  getDefaultPageTypeForOutput,
  isPageTypeCompatibleWithOutput,
} from "../output-contract/index.js";

// ---------------------------------------------------------------------------
// Puck JSON schema validation
// ---------------------------------------------------------------------------
// Validates the structure of Puck editor output JSON.
// This is what gets stored in page_versions.puck_data.
// Note: `id` is optional here because the editor may not always set it.
// For AI-generated output, use aiGenerationResponseSchema (page-config.ts)
// which requires `id` on every component.

const puckContentItemSchema: z.ZodType = z.lazy(() =>
  z.object({
    type: z.string().min(1),
    props: z
      .record(z.unknown())
      .and(z.object({ id: z.string().optional() }).passthrough()),
  }),
);

const puckZoneSchema = z.array(puckContentItemSchema);

export const puckDataSchema = z.object({
  content: z.array(
    z.object({
      type: z.string().min(1),
      props: z
        .record(z.unknown())
        .and(z.object({ id: z.string().optional() }).passthrough()),
    }),
  ),
  root: z
    .object({
      props: z.record(z.unknown()).optional(),
    })
    .passthrough()
    .optional(),
  zones: z.record(puckZoneSchema).optional(),
});

export type PuckData = z.infer<typeof puckDataSchema>;

// ---------------------------------------------------------------------------
// Page settings validation
// ---------------------------------------------------------------------------

export const pageSettingsSchema = z
  .object({
    favicon: z.string().url().optional(),
    customCss: z.string().max(50_000).refine(
      (val) => !/<[a-z]/i.test(val),
      "Custom CSS must not contain HTML tags",
    ).refine(
      (val) => !/@import\b/i.test(val),
      "Custom CSS must not use @import",
    ).refine(
      (val) => !/url\s*\(/i.test(val),
      "Custom CSS must not use url()",
    ).refine(
      (val) => !/expression\s*\(/i.test(val),
      { message: "Custom CSS cannot use expression() — this is a security risk" },
    ).refine(
      (val) => !/-moz-binding\s*:/i.test(val),
      { message: "Custom CSS cannot use -moz-binding — this is a security risk" },
    ).refine(
      (val) => !/behavior\s*:/i.test(val),
      { message: "Custom CSS cannot use behavior — this is a security risk" },
    ).optional(),
    // headScripts and bodyEndScripts accept raw HTML — admin role enforcement required at the API layer.
    // Only script, link, meta, and style tags are allowed (no iframes, objects, etc.)
    headScripts: z.string().max(10_000).refine(
      (val) => !/<(iframe|object|embed|form|base)\b/i.test(val),
      "Head scripts must not contain iframe, object, embed, form, or base tags",
    ).optional(),
    bodyEndScripts: z.string().max(10_000).refine(
      (val) => !/<(iframe|object|embed|form|base)\b/i.test(val),
      "Body end scripts must not contain iframe, object, embed, form, or base tags",
    ).optional(),
    googleFonts: z.array(z.string()).max(10).optional(),
    mood: z.enum(["minimal", "bold", "elegant", "playful", "dark"]).optional(),
  })
  .passthrough();

// ---------------------------------------------------------------------------
// SEO meta validation
// ---------------------------------------------------------------------------

export const seoMetaSchema = z
  .object({
    title: z.string().max(70).optional(),
    description: z.string().max(160).optional(),
    ogImage: z.string().url().optional(),
    ogType: z.string().optional(),
    twitterCard: z
      .enum(["summary", "summary_large_image", "app", "player"])
      .optional(),
    canonicalUrl: z.string().url().optional(),
    robots: z.string().max(100).regex(/^[a-z,\s-]+$/i, "Robots must contain only directives").optional(),
    jsonLd: z.array(z.record(z.unknown())).optional(),
  })
  .passthrough();

// ---------------------------------------------------------------------------
// Slug validation
// ---------------------------------------------------------------------------

export const slugSchema = z
  .string()
  .min(1)
  .max(200)
  .regex(
    /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
    "Slug must be lowercase alphanumeric with hyphens",
  );

// ---------------------------------------------------------------------------
// Create page input validation
// ---------------------------------------------------------------------------

export const createPageSchema = z.object({
  slug: slugSchema,
  title: z.string().min(1).max(255),
  description: z.string().max(1000).optional(),
  outputKey: z.enum(CANONICAL_OUTPUT_KEYS).optional(),
  pageType: z
    .enum(PAGE_TYPES as unknown as [string, ...string[]])
    .optional(),
  folderId: z.string().uuid().optional(),
  siteId: z.string().uuid().optional(),
  settings: pageSettingsSchema.optional(),
  seoMeta: seoMetaSchema.optional(),
}).superRefine((value, ctx) => {
  if (value.outputKey && !value.pageType && !getDefaultPageTypeForOutput(value.outputKey)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `output "${value.outputKey}" cannot be persisted as a page until a canonical pageType mapping exists`,
      path: ["outputKey"],
    });
  }

  if (value.outputKey && value.pageType) {
    const pageType = value.pageType as PageType;
    if (isPageTypeCompatibleWithOutput(value.outputKey, pageType)) {
      return;
    }

    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `pageType "${value.pageType}" is not compatible with output "${value.outputKey}"`,
      path: ["pageType"],
    });
  }
});

// ---------------------------------------------------------------------------
// Update page input validation
// ---------------------------------------------------------------------------

export const updatePageSchema = z.object({
  slug: slugSchema.optional(),
  title: z.string().min(1).max(255).optional(),
  description: z.string().max(1000).optional(),
  status: z.enum(PAGE_STATUSES as unknown as [string, ...string[]]).optional(),
  outputKey: z.enum(CANONICAL_OUTPUT_KEYS).optional(),
  pageType: z
    .enum(PAGE_TYPES as unknown as [string, ...string[]])
    .optional(),
  folderId: z.string().uuid().nullable().optional(),
  settings: pageSettingsSchema.optional(),
  seoMeta: seoMetaSchema.optional(),
}).superRefine((value, ctx) => {
  if (value.outputKey && !value.pageType && !getDefaultPageTypeForOutput(value.outputKey)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `output "${value.outputKey}" cannot be persisted as a page until a canonical pageType mapping exists`,
      path: ["outputKey"],
    });
  }

  if (value.outputKey && value.pageType) {
    const pageType = value.pageType as PageType;
    if (isPageTypeCompatibleWithOutput(value.outputKey, pageType)) {
      return;
    }

    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `pageType "${value.pageType}" is not compatible with output "${value.outputKey}"`,
      path: ["pageType"],
    });
  }
});

// ---------------------------------------------------------------------------
// Save version input validation
// ---------------------------------------------------------------------------

export const saveVersionSchema = z.object({
  puckData: puckDataSchema,
  bindings: z.record(z.unknown()).optional(),
  generationSource: z.enum(["ai", "manual", "duplicate", "autosave"]).optional(),
  aiPrompt: z.string().max(10_000).optional(),
});

// ---------------------------------------------------------------------------
// Brand settings validation
// ---------------------------------------------------------------------------

export const colorValueSchema = z
  .string()
  .max(25)
  .regex(
    /^(#([0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})|rgb\(\d{1,3},\s*\d{1,3},\s*\d{1,3}\)|rgba\(\d{1,3},\s*\d{1,3},\s*\d{1,3},\s*(0|1|0?\.\d+)\)|hsl\(\d{1,3},\s*\d{1,3}%?,\s*\d{1,3}%?\)|hsla\(\d{1,3},\s*\d{1,3}%?,\s*\d{1,3}%?,\s*(0|1|0?\.\d+)\))$/,
    "Must be a valid color (hex, rgb, rgba, hsl, or hsla)",
  );

export const brandColorsSchema = z
  .object({
    primary: colorValueSchema.optional(),
    secondary: colorValueSchema.optional(),
    accent: colorValueSchema.optional(),
    text: colorValueSchema.optional(),
    background: colorValueSchema.optional(),
  })
  .passthrough();

export const brandFontSchema = z.object({
  family: z.string().min(1).max(100),
  weight: z.string().max(10).optional(),
});

export const updateBrandSchema = z.object({
  logoUrl: z.string().url().nullable().optional(),
  iconUrl: z.string().url().nullable().optional(),
  colors: brandColorsSchema.optional(),
  fonts: z
    .object({
      headings: brandFontSchema.optional(),
      body: brandFontSchema.optional(),
    })
    .passthrough()
    .optional(),
  businessName: z.string().max(200).nullable().optional(),
  industry: z.string().max(200).nullable().optional(),
  brandVoice: z
    .enum(["professional", "casual", "playful", "authoritative", "friendly"])
    .nullable()
    .optional(),
  targetAudience: z.string().max(500).nullable().optional(),
  tagline: z.string().max(300).nullable().optional(),
  socialLinks: z
    .object({
      facebook: z.string().url().optional(),
      instagram: z.string().url().optional(),
      linkedin: z.string().url().optional(),
      twitter: z.string().url().optional(),
      tiktok: z.string().url().optional(),
      youtube: z.string().url().optional(),
    })
    .passthrough()
    .optional(),
  guidelinesUrl: z.string().url().nullable().optional(),
});
